import {
    supabase,
    isSupabaseConfigured,
    sanitizeSupabaseError,
} from "@/utils/supabase/client";
import { PACK_SIZE } from "@/data/merchCups";
import { calculateOrderTotal } from "@/lib/merchPricing";
import { buildMerchInvoice } from "@/lib/mpesaPayment";

/** Expected total from order line items (ignores tampered total_kes). */
export function expectedMerchOrderTotalKes(order) {
    const totals = calculateOrderTotal(
        Number(order?.packs) || 0,
        order?.cup_teams || [],
    );
    return totals.subtotalKes;
}

/**
 * Before marking paid, ensure stored total matches server pricing rules.
 * If gaspayments exposes an amount field, it must be >= expected total.
 */
export async function verifyMerchPaymentBeforeFulfill(
    externalReference,
    order,
) {
    const expected = expectedMerchOrderTotalKes(order);
    if (expected < 1) {
        throw new Error("Invalid order total.");
    }

    const stored = Number(order?.total_kes) || 0;
    if (stored > 0 && stored !== expected) {
        throw new Error(
            "Order total mismatch. Contact support with your order ID.",
        );
    }

    if (!supabase) {
        return;
    }

    const { data: payment, error } = await supabase
        .from("gaspayments")
        .select("*")
        .eq("user_reference", externalReference)
        .maybeSingle();

    if (error || !payment) {
        return;
    }

    const paidAmount = Number(
        payment.amount ??
            payment.Amount ??
            payment.transaction_amount ??
            payment.kes_amount ??
            0,
    );
    if (paidAmount > 0 && paidAmount < expected) {
        throw new Error(
            `Payment amount (KES ${paidAmount}) is less than order total (KES ${expected}).`,
        );
    }
}

function shortOrderId() {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();
}

export { isSupabaseConfigured };

export async function createMerchOrderSupabase({
    customer_name,
    phone,
    whatsapp,
    color,
    packs,
    cup_teams,
}) {
    if (!supabase) {
        throw new Error("Supabase is not configured.");
    }

    const totals = calculateOrderTotal(packs, cup_teams);
    const id = shortOrderId();
    const external_reference = buildMerchInvoice();

    const row = {
        id,
        external_reference,
        status: "pending",
        customer_name,
        phone,
        whatsapp,
        color,
        packs,
        cups: totals.cups,
        cup_teams,
        total_kes: totals.subtotalKes,
        payment_method: "mpesa",
        mpesacode: null,
        mpesa_receipt: null,
    };

    const { data, error } = await supabase
        .from("merch_orders")
        .insert(row)
        .select()
        .single();

    if (error) {
        throw new Error(sanitizeSupabaseError(error, "Failed to save order."));
    }

    return data;
}

export async function getMerchOrderSupabase(orderId) {
    if (!supabase) {
        throw new Error("Supabase is not configured.");
    }

    const { data, error } = await supabase
        .from("merch_orders")
        .select("*")
        .eq("id", orderId.toUpperCase())
        .maybeSingle();

    if (error) {
        throw new Error(sanitizeSupabaseError(error, "Could not load order."));
    }
    if (!data) {
        throw new Error("Order not found.");
    }

    return normalizeOrder(data);
}

export async function markMerchOrderPaidSupabase(
    externalReference,
    paymentReference,
    orderForVerify = null,
) {
    if (!supabase) {
        throw new Error("Supabase is not configured.");
    }

    const ref = String(paymentReference || "").trim();
    if (!ref) {
        throw new Error("Missing payment reference.");
    }

    if (orderForVerify) {
        await verifyMerchPaymentBeforeFulfill(externalReference, orderForVerify);
    }

    const { data, error } = await supabase
        .from("merch_orders")
        .update({
            status: "paid",
            payment_method: "mpesa",
            mpesacode: ref,
            mpesa_receipt: ref,
            paid_at: new Date().toISOString(),
        })
        .eq("external_reference", externalReference)
        .select()
        .single();

    if (error) {
        throw new Error(
            sanitizeSupabaseError(error, "Failed to update order."),
        );
    }

    return normalizeOrder(data);
}

/** Map DB row to shape used by UI (supports mpesacode or mpesa_receipt). */
function normalizeOrder(row) {
    return {
        ...row,
        status: String(row.status).toLowerCase() === "paid" ? "paid" : "pending",
        mpesa_receipt: row.mpesa_receipt || row.mpesacode || null,
        external_reference: row.external_reference,
    };
}

export { PACK_SIZE };
